import React, { useState } from 'react';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useAppDataContext } from '../context/AppDataContext';

interface Props {
  onAccept: () => void;
  onDecline: () => void;
}

const PatientConsent: React.FC<Props> = ({ onAccept, onDecline }) => {
  const { profile } = useAppDataContext();
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    if (!profile) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          privacy_accepted: true,
          privacy_accepted_at: new Date().toISOString()
        })
        .eq('id', profile.id);

      if (error) throw error;
      
      onAccept();
    } catch (err) {
      console.error('Error accepting privacy policy:', err);
      alert('אירעה שגיאה. נסה שוב.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-background flex flex-col justify-center items-center p-6 dir-rtl">
      <div className="max-w-md w-full bg-card border border-border rounded-3xl p-8 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 flex flex-col items-center text-center">
          <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mb-6 text-primary">
            <ShieldCheck size={40} />
          </div>
          
          <h1 className="text-2xl font-bold text-foreground mb-4">
            פרטיות ובטיחות הנתונים שלך
          </h1>
          
          <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
            ReMind היא כלי עזר שהמטפל/ת שלך משתמש/ת בו בין המפגשים. כדי שזה יעבוד, נשמר עליך מידע מזהה, כולל מידע רגיש על הטיפול. לפני שממשיכים, חשוב שתדע/י מה נשמר ולאן הוא עובר.
          </p>

          <div className="bg-muted border border-border rounded-xl p-4 text-xs text-muted-foreground text-right mb-8 w-full space-y-2">
            <p><strong>מה נשמר:</strong> שמך, גילך ותיאור קצר של נושא הטיפול שהזין המטפל/ת; המשימות שלך והאם ביצעת אותן; דיווחי מצב רוח ויומן; מועדי כניסה לאפליקציה; וסיכומי מפגשים שהמטפל/ת כותב/ת.</p>
            <p><strong>מי רואה את המידע:</strong> את/ה, המטפל/ת שלך, ומנהל/ת הקליניקה שבה הוא/היא עובד/ת.</p>
            <p><strong>עיבוד בבינה מלאכותית:</strong> כשהמטפל/ת מבקש/ת הצעות למשימות או הערכת מעורבות, סיכום המפגש ונתוני השימוש שלך נשלחים לשירות Azure OpenAI של Microsoft. ה-AI רק מציע; את ההחלטות מקבל/ת המטפל/ת.</p>
            <p><strong>איפה זה נשמר:</strong> אצל ספקי ענן (Supabase, Vercel ו-Microsoft Azure), ייתכן שמחוץ לישראל. המידע מועבר בחיבור מוצפן ונשמר במאגר מוצפן.</p>
            <p><strong>הזכויות שלך:</strong> לעיין במידע, לתקן אותו ולבקש את מחיקתו, דרך המטפל/ת שלך. אפשר לסרב, ואז לא תוכל/י להשתמש באפליקציה.</p>
          </div>

          <div className="w-full space-y-3">
            <button
              onClick={handleAccept}
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-4 rounded-xl transition-all shadow-lg shadow-primary/20"
            >
              {loading ? 'מאשר...' : 'קראתי, ואני מאשר/ת'}
            </button>
            <button
              onClick={onDecline}
              disabled={loading}
              className="w-full bg-transparent text-muted-foreground hover:text-foreground font-medium py-3 rounded-xl transition-all text-sm flex items-center justify-center gap-2"
            >
              <ArrowLeft size={16} /> אינני מסכים/ה, התנתק
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientConsent;
